import { useEffect, useState } from "react";
import { Star, Trash2, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "../ui/sonner";

const MODERATION_COLORS = { visible: "bg-midnight-black text-clean-white", hidden: "bg-soft-gray text-midnight-black/70", flagged: "bg-red-100 text-red-700" };

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: n || 0 }).map((_, i) => (
      <Star key={i} className="w-3.5 h-3.5 text-stranger-orange fill-stranger-orange" />
    ))}
  </div>
);

export const StoriesTab = () => {
  const [reviews, setReviews] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    api.get("/admin/reviews").then(({ data }) => setReviews(data));
  };

  useEffect(() => {
    load();
  }, []);

  const seedSampleStories = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/reviews/seed-sample-stories");
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSeeding(false);
    }
  };

  const toggleFeature = async (id) => {
    await api.put(`/reviews/${id}/feature`);
    load();
  };

  const moderate = async (id, action) => {
    await api.put(`/admin/reviews/${id}/moderate`, null, { params: { action } });
    toast.success(`Review ${action === "restore" ? "restored" : action + "d"}`);
    load();
  };

  const removeReview = async (id) => {
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Story removed");
    load();
  };

  if (reviews === null) return <p className="font-medium text-midnight-black/60">Loading stories...</p>;

  return (
    <div data-testid="admin-stories-tab">
      <div className="flex items-center justify-end mb-6">
        <button
          data-testid="seed-sample-stories-button"
          onClick={seedSampleStories}
          disabled={seeding}
          className="flex items-center gap-2 text-xs font-bold tracking-widest px-5 py-3 rounded-full border-2 border-stranger-orange text-stranger-orange hover:bg-stranger-orange hover:text-clean-white transition-colors disabled:opacity-50"
        >
          <Sparkles className="w-3.5 h-3.5" /> {seeding ? "SEEDING..." : "SEED SAMPLE STORIES"}
        </button>
      </div>

      {reviews.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-admin-stories">No stories yet.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-stories-list">
          {reviews.map((r) => {
        const modStatus = r.moderation_status || "visible";
        const photographerRating = r.photographer_rating ?? r.rating;
        const photographerComment = r.photographer_comment ?? r.comment;
        return (
          <div key={r.id} className="border border-soft-gray rounded-xl p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`admin-story-row-${r.id}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${MODERATION_COLORS[modStatus]}`} data-testid={`admin-story-moderation-${r.id}`}>{modStatus}</span>
                <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/60">{r.stranger_name} · {r.session_title}</p>
              </div>
              {photographerComment && (
                <div className="mb-2">
                  <p className="text-[10px] font-bold uppercase text-midnight-black/40">Photographer</p>
                  <Stars n={photographerRating} />
                  <p className="text-sm font-medium text-midnight-black/80">&ldquo;{photographerComment}&rdquo;</p>
                </div>
              )}
              {r.experience_comment && (
                <div className="mb-2">
                  <p className="text-[10px] font-bold uppercase text-midnight-black/40">Experience</p>
                  <Stars n={r.experience_rating} />
                  <p className="text-sm font-medium text-midnight-black/80">&ldquo;{r.experience_comment}&rdquo;</p>
                </div>
              )}
              {r.private_feedback && <p className="text-xs text-midnight-black/50 mt-1"><strong>Private feedback:</strong> {r.private_feedback}</p>}
              {r.match_feedback && <p className="text-xs text-midnight-black/50 mt-1"><strong>Match feedback:</strong> {r.match_feedback}</p>}
              {(r.would_recommend !== undefined || r.would_participate_again !== undefined) && (
                <p className="text-[10px] font-bold uppercase tracking-wide text-midnight-black/40 mt-2">
                  {r.would_recommend != null && `Would recommend: ${r.would_recommend ? "Yes" : "No"} `}
                  {r.would_participate_again != null && `· Would participate again: ${r.would_participate_again ? "Yes" : "No"}`}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-3">
                <button data-testid={`admin-toggle-feature-${r.id}`} onClick={() => toggleFeature(r.id)} className="text-[11px] font-bold tracking-widest text-stranger-orange hover:underline">
                  {r.is_featured ? "UNFEATURE" : "FEATURE"}
                </button>
                <button data-testid={`admin-delete-story-${r.id}`} onClick={() => removeReview(r.id)} className="text-midnight-black/50 hover:text-red-600" aria-label="Delete story">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                {modStatus !== "visible" && (
                  <button data-testid={`admin-restore-review-${r.id}`} onClick={() => moderate(r.id, "restore")} className="text-[10px] font-bold uppercase tracking-wide text-midnight-black/60 hover:text-midnight-black">RESTORE</button>
                )}
                {modStatus !== "hidden" && (
                  <button data-testid={`admin-hide-review-${r.id}`} onClick={() => moderate(r.id, "hide")} className="text-[10px] font-bold uppercase tracking-wide text-midnight-black/60 hover:text-midnight-black">HIDE</button>
                )}
                {modStatus !== "flagged" && (
                  <button data-testid={`admin-flag-review-${r.id}`} onClick={() => moderate(r.id, "flag")} className="text-[10px] font-bold uppercase tracking-wide text-red-500 hover:text-red-700">FLAG</button>
                )}
              </div>
            </div>
          </div>
        );
          })}
        </div>
      )}
    </div>
  );
};
